import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  cpGetLineMessagingStatus,
  cpConnectLineMessaging,
  cpConnectLineMessagingByCredentials,
  cpDisconnectLineMessaging,
  cpUpdateLineNotificationPreferences,
  type LineNotificationPreferencesUpdate,
} from '@/lib/api/controlPlaneClient'
import { useAuthStore } from '@/lib/store/authStore'

export const LINE_MESSAGING_STATUS_KEY = ['line-messaging-status'] as const

export function useLineMessagingConfig(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient()
  // Scope status to the selected store so multi-store accounts see the right OA
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)

  const statusQuery = useQuery({
    queryKey: [...LINE_MESSAGING_STATUS_KEY, activeStorefrontKey],
    queryFn: () => cpGetLineMessagingStatus(),
    staleTime: 5 * 60 * 1000, // 5 min — sidebar uses this, avoid hammering
    enabled: options?.enabled !== false,
  })

  const connectMutation = useMutation({
    mutationFn: (token: string) => cpConnectLineMessaging(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINE_MESSAGING_STATUS_KEY })
    },
  })

  const connectByCredentialsMutation = useMutation({
    mutationFn: ({ channelId, channelSecret }: { channelId: string; channelSecret: string }) =>
      cpConnectLineMessagingByCredentials(channelId, channelSecret),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINE_MESSAGING_STATUS_KEY })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: () => cpDisconnectLineMessaging(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINE_MESSAGING_STATUS_KEY })
    },
  })

  const updatePreferencesMutation = useMutation({
    mutationFn: (prefs: LineNotificationPreferencesUpdate) =>
      cpUpdateLineNotificationPreferences(prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LINE_MESSAGING_STATUS_KEY })
    },
  })

  return {
    status: statusQuery.data ?? null,
    isLoading: statusQuery.isLoading,
    connect: connectMutation.mutate,
    isConnecting: connectMutation.isPending,
    connectByCredentials: connectByCredentialsMutation.mutate,
    isConnectingByCredentials: connectByCredentialsMutation.isPending,
    connectByCredentialsError: connectByCredentialsMutation.error?.message ?? null,
    disconnect: disconnectMutation.mutate,
    isDisconnecting: disconnectMutation.isPending,
    connectError: connectMutation.error?.message ?? null,
    disconnectError: disconnectMutation.error?.message ?? null,
    updatePreferences: updatePreferencesMutation.mutate,
    isUpdatingPreferences: updatePreferencesMutation.isPending,
  }
}
